import { Injectable } from '@angular/core';
import { UrlSegments } from '../../jurisdiction/jurisdiction.constants';
import { CommunicationService } from '../../services/communication.service';
import { TechnicalData } from '../../+grid-application-shared/entities/technicalData';
import { Observable } from 'rxjs';
import { UpdateApplicationStatusRequest } from '../../jurisdiction/jurisdiction.domain';
import { DocumentsSectionsState } from '../../+grid-application-shared/entities/sections-state/documentsSectionsState';
import {
  ENERGY_TYPES,
  MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION,
} from '../../+grid-application-shared/constants/grid-application-constants';
import { map } from 'rxjs/operators';
import { GridCompFeedInApplication } from '../entities/gridCompFeedInApplication';
import { GridApplicationService } from '../../+grid-application-shared/services/grid-application.service';
import { AddressAndPersonalData } from '../../+grid-application-shared/entities/personal-data/addressAndPersonalData';
import { PersonalDataSectionsState } from 'src/app/+grid-application-shared/entities/sections-state/personalDataSectionsState';
import { GridCompTechnicalSpecificationsSectionsState } from '../entities/sections-state/gridCompTechnicalSpecificationsSectionsState';
import { GridCompatibilityProcessStatus } from '../entities/enums/gridCompatibilityProcessStatus';

@Injectable()
export class GridCompatibilityService extends GridApplicationService<GridCompFeedInApplication> {
  private readonly gridCompApplicationUrl: string = UrlSegments.GridCompApplication;
  private readonly gridCompFeedInApplicationUrl: string = UrlSegments.GridCompFeedInApplication;
  private readonly applicationUrl: string = UrlSegments.Application;
  private readonly operatorAddressUrl: string = UrlSegments.CreatePowerPlantOperatorAddress;

  constructor(communicationService: CommunicationService) {
    super(communicationService, {
      technicalData: UrlSegments.GridCompApplication,
      uploadDocument: UrlSegments.UploadGridCompDocuments,
    });
  }

  public areAllSectionsReviewed(): boolean {
    const gridCompFeedInApplication = this.application;

    if (
      JSON.parse(String(gridCompFeedInApplication.technical_data.has_energy_producer)) &&
      gridCompFeedInApplication.technical_specifications_sections_state.is_existing_energy_producer_locked === null
    ) {
      return false;
    }

    if (
      ENERGY_TYPES[gridCompFeedInApplication.technical_data.existing_energy_producers[0].energy_type] === ENERGY_TYPES.PHOTOVOLTAICS &&
      gridCompFeedInApplication.technical_specifications_sections_state.is_detailed_info_photovoltaics_locked === null
    ) {
      return false;
    }

    if (
      ENERGY_TYPES[gridCompFeedInApplication.technical_data.existing_energy_producers[0].energy_type] > ENERGY_TYPES.STORAGE &&
      gridCompFeedInApplication.technical_specifications_sections_state.is_detailed_info_energy_producer_locked === null
    ) {
      return false;
    }

    if (
      (ENERGY_TYPES[gridCompFeedInApplication.technical_data.existing_energy_producers[0].energy_type] === ENERGY_TYPES.STORAGE ||
        JSON.parse(String(gridCompFeedInApplication.technical_data.has_power_storage))) &&
      gridCompFeedInApplication.technical_specifications_sections_state.is_detailed_info_storage_locked === null
    ) {
      return false;
    }

    if (
      !JSON.parse(String(gridCompFeedInApplication.businesspartner.owner_is_invoice_recepient)) &&
      gridCompFeedInApplication.personal_data_sections_state.is_invoice_address_locked === null
    ) {
      return false;
    }

    if (
      !JSON.parse(String(gridCompFeedInApplication.businesspartner.owner_is_pp_operator)) &&
      gridCompFeedInApplication.personal_data_sections_state.is_operator_of_power_plant_locked === null
    ) {
      return false;
    }

    const totalFeedInPower = gridCompFeedInApplication.technical_data.existing_energy_producers[0].total_feed_in_power;
    const areDocsUploaded =
      !!gridCompFeedInApplication.technical_data.grid_power_plant_protection_data.integrated_grid_protection_documents?.length;
    if (
      ((totalFeedInPower <= MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION && areDocsUploaded) ||
        totalFeedInPower > MIN_TOTAL_FEED_IN_POWER_FOR_CENTRAL_GRID_PROTECTION) &&
      gridCompFeedInApplication.technical_specifications_sections_state.is_detailed_info_grid_power_plant_locked === null
    ) {
      return false;
    }

    return (
      gridCompFeedInApplication.technical_specifications_sections_state.is_base_data_locked !== null &&
      gridCompFeedInApplication.technical_specifications_sections_state.is_type_of_feed_in_locked !== null &&
      gridCompFeedInApplication.technical_specifications_sections_state.is_feed_in_controllability_locked !== null &&
      gridCompFeedInApplication.technical_specifications_sections_state.is_concept_of_measurement_locked !== null &&
      gridCompFeedInApplication.technical_specifications_sections_state.is_meter_operation_locked !== null &&
      gridCompFeedInApplication.personal_data_sections_state.is_connection_address_locked !== null &&
      gridCompFeedInApplication.personal_data_sections_state.is_subscriber_owner_locked !== null &&
      gridCompFeedInApplication.documents_sections_state?.is_documents_locked !== null
    );
  }

  protected fetchApplication(applicationId: number): Observable<GridCompFeedInApplication> {
    return this.communicationService.getData<GridCompFeedInApplication[]>(`${this.gridCompFeedInApplicationUrl}${applicationId}`).pipe(
      map(response => {
        const application = response[0];

        application.technical_specifications_sections_state =
          Object.keys(application.technical_specifications_sections_state).length === 0
            ? new GridCompTechnicalSpecificationsSectionsState()
            : new GridCompTechnicalSpecificationsSectionsState(application.technical_specifications_sections_state);

        application.personal_data_sections_state =
          Object.keys(application.personal_data_sections_state).length === 0
            ? new PersonalDataSectionsState(null)
            : new PersonalDataSectionsState(application.personal_data_sections_state);

        application.documents_sections_state =
          Object.keys(application.documents_sections_state).length === 0
            ? new DocumentsSectionsState(null)
            : new DocumentsSectionsState(application.documents_sections_state);

        return application;
      })
    );
  }

  public createPpOperatorAddress(address: AddressAndPersonalData): Observable<any> {
    return this.communicationService.postData(this.operatorAddressUrl, address);
  }

  public updatePpOperatorAddress(address: AddressAndPersonalData): Observable<any> {
    return this.communicationService.updateData(this.operatorAddressUrl, address);
  }

  public deletePpOperatorAddress(operatorAddressId: number): Observable<any> {
    return this.communicationService.deleteData(`${this.operatorAddressUrl}?id=${operatorAddressId}`);
  }

  public updateGridCompApplication(technicalData: TechnicalData): Observable<any> {
    return this.communicationService.updateData(this.gridCompApplicationUrl, technicalData);
  }

  public updateGridCompFeedInApplication(updateRequest: UpdateApplicationStatusRequest, applicationId: number): Observable<any> {
    return this.communicationService.updateData(`${this.gridCompFeedInApplicationUrl}${applicationId}`, updateRequest);
  }

  public getApplicationDocuments(applicationId: number): Observable<any> {
    return this.communicationService.getData(`${this.applicationUrl}/documents?applicationId=${applicationId}`);
  }

  public getByExternalIdAndStatus(
    externalId: string,
    processStatus: GridCompatibilityProcessStatus
  ): Observable<GridCompFeedInApplication> {
    return this.communicationService.getData<GridCompFeedInApplication>(
      `${UrlSegments.GridCompFeedInApplicationByExternalId}/${externalId}/processStatus/${processStatus}`
    );
  }
}
